import { PrimaryButton, SecondaryButton } from '@/components/Button';
import { Dialog } from '@/components/Dialog';
import { Input } from '@/components/ui/input';
import { useEffect, useState } from 'react';
import TrashIcon from '@/assets/icons/Trash.svg';
import { createSecretKey, deleteSecretKey, editSecretKey } from '@/service/apiKey';
import { toast } from 'sonner';
import { getOrganization } from '@/service/overview';
import { useRequest } from 'ahooks';
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card';
import BellRingIcon from '@/assets/icons/BellRing.svg';
import { DOCUMENTATION_URL } from '@/config';

export const useApiKeys = () => {
  const { data } = useRequest(getOrganization);
  const isVerified = data?.is_verified;
  const showVerifiedModal = async () => {
    await Dialog.info({
      contentJSX: () => {
        return (
          <div className="flex flex-col items-center gap-8">
            <div className="w-full">
              <div className="w-fit rounded-full p-5 bg-[#FF5B271A] inline-block text-left">
                <BellRingIcon />
              </div>
            </div>

            <div>
              <CardTitle className="">Verification in Progress</CardTitle>
              <CardDescription className="mb-6 text-left">
                We are reviewing your application and will send you an email to confirm your verification shortly.
              </CardDescription>
            </div>

            <PrimaryButton
              onClick={() => {
                window.location.href = DOCUMENTATION_URL;
              }}
              className="w-full"
            >
              Read Documentation
            </PrimaryButton>
          </div>
        );
      },
    });
  };
  const createApiKey = async () => {
    if (!isVerified) {
      await showVerifiedModal();
      return;
    }
    const res = await Dialog.info({
      contentJSX: ({ onClose, resolve }) => {
        const [name, setName] = useState<string | undefined>(undefined);
        const [isLoading, setIsLoading] = useState(false);

        return (
          <div className="flex flex-col gap-8">
            <div>
              <h1 className="text-[20px]">Create new secret key</h1>
              <div className="text-[#747479] mt-2">
                Do not share your API key with others or expose it in the browser or other client-side code. To protect your account's
                security, Jen may automatically disable any API key that has leaked publicly.
              </div>
            </div>
            <div>
              <div>Name</div>
              <Input
                value={name}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setName(event.target.value);
                }}
                className="mt-2"
                placeholder="My API Key"
              />
            </div>
            <div className="grid grid-cols-2 justify-between gap-2">
              <SecondaryButton onClick={() => onClose()}>Cancel</SecondaryButton>
              <PrimaryButton
                isLoading={isLoading}
                onClick={async () => {
                  if (name?.trim()) {
                    try {
                      setIsLoading(true);
                      await createSecretKey({ name });
                      onClose();
                      resolve(name);
                    } catch (error) {
                      toast.error('network error,please try again');
                    } finally {
                      setIsLoading(false);
                    }
                  }
                }}
              >
                Create New Key
              </PrimaryButton>
            </div>
          </div>
        );
      },
    });
    return res;
  };
  const editApiKey = async ({ initName, id }: { initName?: string; id: string }) => {
    const res = await Dialog.info({
      contentJSX: ({ onClose, resolve }) => {
        const [name, setName] = useState<string | undefined>(initName || undefined);
        const [isLoading, setIsLoading] = useState(false);
        return (
          <div className="flex flex-col gap-8">
            <div>
              <h1 className="text-[20px]">Edit API Key</h1>
              <div className="text-[#747479] mt-2">Change the name for this API key</div>
            </div>
            <div>
              <div>Name</div>
              <Input
                value={name}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setName(event.target.value);
                }}
                className="mt-2"
                placeholder="My API Key"
              />
            </div>
            <div className="grid grid-cols-2 justify-between gap-2">
              <SecondaryButton onClick={() => onClose()}>Cancel</SecondaryButton>
              <PrimaryButton
                isLoading={isLoading}
                onClick={async () => {
                  if (name?.trim()) {
                    try {
                      setIsLoading(true);
                      await editSecretKey({ name, id });
                      onClose();
                      resolve(name);
                    } catch (error) {
                      toast.error('network error,please try again');
                    } finally {
                      setIsLoading(false);
                    }
                  }
                }}
              >
                Save Change
              </PrimaryButton>
            </div>
          </div>
        );
      },
    });
    return res;
  };
  const deleteApiKey = async ({ id }: { id: string }) => {
    const res = await Dialog.info({
      contentJSX: ({ onClose, resolve }) => {
        const [inputVal, setInput] = useState<string | undefined>(undefined);
        const [isLoading, setIsLoading] = useState(false);
        return (
          <div className="flex flex-col gap-8">
            <div>
              <h1 className="text-[20px]">Delete API Key</h1>
              <div className="text-[#747479] mt-2">
                This API key will immediately be disabled. API requests made using this key will be rejected, which could cause any systems
                still depending on it to break. Once revoked, you'll no longer be able to view or modify this API key.
              </div>
            </div>
            <div>
              <div className="text-[#747479]">Type “DELETE” to remove this key</div>
              <Input
                value={inputVal}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setInput(event.target.value);
                }}
                className="mt-2"
                placeholder="DELETE"
              />
            </div>
            <div className="grid grid-cols-2 justify-between gap-2">
              <SecondaryButton onClick={() => onClose()}>Cancel</SecondaryButton>
              <PrimaryButton
                className="bg-[#bc3229] hover:bg-[#bc3229]"
                isLoading={isLoading}
                preIcon={<TrashIcon fillColor="currentColor" className="" />}
                disabled={inputVal !== 'DELETE'}
                onClick={async () => {
                  try {
                    setIsLoading(true);
                    await deleteSecretKey({ id });
                    onClose();
                    resolve();
                  } catch (error) {
                    toast.error('network error,please try again');
                  } finally {
                    setIsLoading(false);
                  }
                }}
              >
                Delete API Key
              </PrimaryButton>
            </div>
          </div>
        );
      },
    });
    return res;
  };
  return {
    createApiKey,
    editApiKey,
    deleteApiKey,
  };
};
